import { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Logout } from './Functions';

const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes (900 seconds)
const WARNING_TIMEOUT_MS = 5 * 60 * 1000;    // Show warning title during last 5 minutes (300 seconds)
const LAST_ACTIVITY_KEY = 'capitaline_last_activity_time';

export const useAutoLogout = () => {
    const navigate = useNavigate();
    const token = useSelector((state) => state.Authenticationapi?.token);
    const loginType = useSelector((state) => state.LocalValuesData?.loginType) || localStorage.getItem("loginType") || "";
    const userId = sessionStorage.getItem("getuserid");

    // Only active when user is authenticated
    const isAuthenticated = Boolean(token?.access_token || userId);

    const isLoggingOutRef = useRef(false);
    const lastActivityRef = useRef(Date.now());
    const intervalRef = useRef(null);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = Math.max(0, seconds % 60);
        return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    const performLogout = useCallback(async () => {
        if (isLoggingOutRef.current) return;
        isLoggingOutRef.current = true;

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        document.title = "Your session has expired";

        try {
            await Logout(loginType);
        } catch (err) {
            console.error("Auto logout API call failed:", err);
        } finally {
            localStorage.clear();
            sessionStorage.clear();
            alert("Your session has expired due to 15 minutes of inactivity. Please log in again.");
            if (loginType === 'External') {
                window.location.replace("/externallogin");
            } else if (loginType === 'FC') {
                window.location.replace("/facelogin");
            } else {
                window.location.replace("/");
            }
        }
    }, [loginType]);

    const recordActivity = useCallback(() => {
        if (!isAuthenticated || isLoggingOutRef.current) return;

        const now = Date.now();
        // Throttle writing to localStorage to at most once every 1.5 seconds
        if (now - lastActivityRef.current > 1500) {
            lastActivityRef.current = now;
            try {
                localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
            } catch (e) {
                // Ignore storage quota error
            }
        } else {
            lastActivityRef.current = now;
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated) return;

        isLoggingOutRef.current = false;
        const initialNow = Date.now();
        lastActivityRef.current = initialNow;
        try {
            localStorage.setItem(LAST_ACTIVITY_KEY, initialNow.toString());
        } catch (e) {
            // Ignore storage error
        }

        const checkInactivity = () => {
            if (isLoggingOutRef.current) return;

            const storedTimeStr = localStorage.getItem(LAST_ACTIVITY_KEY);
            const storedTime = storedTimeStr ? Number(storedTimeStr) : lastActivityRef.current;
            const now = Date.now();
            const elapsed = now - storedTime;

            if (elapsed >= INACTIVITY_TIMEOUT_MS) {
                performLogout();
                return;
            }

            const remainingMs = INACTIVITY_TIMEOUT_MS - elapsed;
            if (remainingMs <= WARNING_TIMEOUT_MS) {
                const remainingSecs = Math.ceil(remainingMs / 1000);
                document.title = `${formatTime(remainingSecs)} until your session times out!`;
            } else {
                if (document.title.includes("until your session times out")) {
                    document.title = "Capitaline Transfer Pricer";
                }
            }
        };

        // Check every 1 second
        intervalRef.current = setInterval(checkInactivity, 1000);

        // Activity event listeners
        const activityEvents = [
            'mousemove',
            'mousedown',
            'keydown',
            'touchstart',
            'scroll',
            'click',
            'wheel'
        ];

        activityEvents.forEach((event) => {
            window.addEventListener(event, recordActivity, { passive: true });
        });

        // Cross-tab synchronization via localStorage events
        const handleStorageChange = (e) => {
            if (e.key === LAST_ACTIVITY_KEY && e.newValue) {
                lastActivityRef.current = Number(e.newValue);
                if (document.title.includes("until your session times out")) {
                    document.title = "Capitaline Transfer Pricer";
                }
            }
        };
        window.addEventListener('storage', handleStorageChange);

        // Immediate check on tab focus or visibility change (e.g. laptop wake or returning to tab)
        const handleVisibilityOrFocus = () => {
            checkInactivity();
        };
        window.addEventListener('focus', handleVisibilityOrFocus);
        document.addEventListener('visibilitychange', handleVisibilityOrFocus);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }

            activityEvents.forEach((event) => {
                window.removeEventListener(event, recordActivity);
            });
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('focus', handleVisibilityOrFocus);
            document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
        };
    }, [isAuthenticated, performLogout, recordActivity]);
};
